import axios from "axios";
import Links from "@/shared/constants/Links";

/**
 * Defense API hook — managing defenses (e.g. postgraduate defenses) and their
 * roster of qualified students.
 *
 * Access (enforced server-side; the UI mirrors it):
 *  - DEAN_SPGS: create / update details / roster / delete
 *  - HOD + DEAN_SPGS: read + update status
 */

const base = Links.defenses.base;

const Defenses = {
    /**
     * Create a defense. DEAN_SPGS only.
     * @param {{name: string, scheduledAt: string, qualifiedStudentIds?: number[]}} data
     */
    create: async (data) => {
        return axios.post(base, data);
    },

    /**
     * List all defenses. DEAN_SPGS, HOD.
     */
    all: async () => {
        return axios.get(base);
    },

    /**
     * Get a single defense by id. DEAN_SPGS, HOD.
     * @param {number|string} id
     */
    get: async (id) => {
        return axios.get(`${base}/${id}`);
    },

    /**
     * Update a defense's name and scheduledAt only. DEAN_SPGS only.
     * @param {number|string} id
     * @param {{name: string, scheduledAt: string}} data
     */
    updateDetails: async (id, data) => {
        return axios.put(`${base}/${id}`, data);
    },

    /**
     * Update a defense's lifecycle status. DEAN_SPGS, HOD.
     * @param {number|string} id
     * @param {string} status one of SCHEDULED, ONGOING, COMPLETED, CANCELLED
     */
    updateStatus: async (id, status) => {
        return axios.patch(`${base}/${id}/status`, { status });
    },

    /**
     * Add qualified students to a defense roster. DEAN_SPGS only.
     * @param {number|string} id
     * @param {number[]} studentIds user database ids (non-empty)
     */
    addQualifiedStudents: async (id, studentIds) => {
        return axios.post(`${base}/${id}/qualified-students`, { studentIds });
    },

    /**
     * Remove a qualified student from a defense roster. DEAN_SPGS only.
     * @param {number|string} id
     * @param {number|string} studentId the user's database id
     */
    removeQualifiedStudent: async (id, studentId) => {
        return axios.delete(`${base}/${id}/qualified-students/${studentId}`);
    },

    /**
     * Delete a defense and its roster (cascade). DEAN_SPGS only.
     * @param {number|string} id
     */
    delete: async (id) => {
        return axios.delete(`${base}/${id}`);
    },
};

export default Defenses;
